import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { completeGoogleAuth } from '../services/googleSheetsService';
import { useAuth } from '../contexts/AuthContext';

export default function GoogleCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const hasProcessed = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double execution (React StrictMode or re-renders)
      if (hasProcessed.current) {
        return;
      }
      hasProcessed.current = true;
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError('Google authentication was cancelled or failed');
        setIsProcessing(false);
        return;
      }

      if (!code) {
        setError('No authorization code received from Google');
        setIsProcessing(false);
        return;
      }

      try {
        await completeGoogleAuth(code);

        // Refresh user data from server (cookie is already set by backend)
        await refreshUser();

        // Redirect to account page
        setTimeout(() => {
          navigate('/account', { replace: true });
        }, 1500);
      } catch (err: any) {
        console.error('Google auth error:', err);
        setError(err.message || 'Failed to complete Google authentication');
        setIsProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, refreshUser]);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto py-12">
        <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg p-8">
          {isProcessing && !error && (
            <div className="text-center">
              <LoadingSpinner message="Completing Google authentication..." />
            </div>
          )}

          {error && (
            <div className="space-y-4">
              <div className="bg-red-900/50 border border-red-600 rounded-lg p-4 text-red-200">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {error}
                </div>
              </div>
              <button
                onClick={() => navigate('/account')}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Return to Account Settings
              </button>
            </div>
          )}

          {!error && !isProcessing && (
            <div className="space-y-4">
              <div className="bg-green-900/50 border border-green-600 rounded-lg p-4 text-green-200">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Google account connected successfully!
                </div>
              </div>
              <p className="text-slate-400 text-center">Redirecting to your account...</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
